import { QueryList } from '@angular/core';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { Observable, Subscription } from 'rxjs';

import { EditableColumn, Table, TableLazyLoadEvent } from 'primeng/table';

import { ColumnSettings } from '../common/models';
import { LoadDataEvent, PagedData } from '../lazy-loading/models';

@Component({
  selector: 'sabis-grid2',
  templateUrl: './sabis-grid2.component.html',
  styleUrls: ['./sabis-grid2.component.scss']
})
export class SabisGrid2Component implements OnChanges {
  @Input('scColumns') scColumns: ColumnSettings[] = [];
  @Input('scData') entries: any[] = [];
  @Output('scOnDataChanged') scOnDataChanged: EventEmitter<any[]> = new EventEmitter<any[]>();
  @Output('scSelectionChanged') scSelectionChanged: EventEmitter<any[]> = new EventEmitter<any[]>();
  @Input('scSelectionEnabled') scSelectionEnabled: boolean = false;
  @Input('scIsLazy') scIsLazy: boolean = true;
  @Input('scDataLoader') dataLoader?: (event: LoadDataEvent) => Observable<PagedData<any>>;
  @Input('scPageSize') scPageSize: number = 20;
  @Input('scClass') scClass: string = '';
  @Input('scToggleColumns') scToggleColumns: boolean = false;
  @Input('scReorderColumns') scReorderColumns: boolean = false;
  @Input('scIsSearchable') scIsSearchable: boolean = true; // show/hide filters

  @ViewChildren(EditableColumn) editableColumns?: QueryList<EditableColumn>;

  @ViewChild('table') table!: Table;

  public totalRecords: number = 100;

  public globalFilterFields: string[] = [];
  public loading: boolean = true;

  public initialLoadedEntries: any[] = [];
  public loadedEntries: any[] = [];
  public selectedEntries: any[] = [];

  public typesMappings: string[] = ['', 'text', 'numeric', 'boolean', 'date', 'time', 'combo', 'multi', 'action', 'icon'];

  private _lastLoadRequest: Subscription = new Subscription();
  private _lastLoadEvent: TableLazyLoadEvent | null = null;

  public selectedColumns!: ColumnSettings[];
  public columnsOptions!: ColumnSettings[];
  public gridColumns: ColumnSettings[] = [];

  constructor() {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entries'] || changes['scIsLazy']) {
      // handle static data - not lazy loaded
      if (!this.scIsLazy) {
        this.loadedEntries = this.entries;
        this.loading = false;
        this.totalRecords = this.entries.length;
      }
    }

    //Setting the values for the different column arrays
    if (this.scToggleColumns) {
      //columnOptions is used to display the options inside the multiselect dropdown
      this.columnsOptions = this.scColumns.filter(column => column.title);
      //selectedColumns is for the chosen columns (appears as chips)
      //using seperate columns for selection and for options is mandatory
      //so the unselected columns still appear in the dropdown after being deselected
      this.selectedColumns = this.scColumns.filter(column => column.title && !column.hiddenByDefault);
      //this function sets the gridColumns array as a concatenation of the selectedColumns Array and the columns that are filtered from selectedColumns array
      this.onSelectedColumnsChanged();
    } else {
      this.gridColumns = [...this.scColumns]
    }
  }

  public onSelectedColumnsChanged(): void {
    let fieldsOrder: string[];
    //this array is used to store the fields of the gridColumns following their index order
    //on init this array will be empty because the gridColumns will also be empty
    fieldsOrder = this.scColumns.map(col => col.field);
    //gridColumns will be empty because its being set right after fieldOrder gets set
    this.gridColumns = [...this.selectedColumns, ...this.scColumns.filter(column => !column.title)];
    if (fieldsOrder.length === 0) {
      //when the fieldsOrder array is empty the gridColumns are sorted the same as the main columns array which is scColumns
      this.gridColumns.sort((a, b) => {
        const indexA = this.scColumns.findIndex(col => col.field === a.field);
        const indexB = this.scColumns.findIndex(col => col.field === b.field);
        return indexA - indexB;
      });
    } else {
      //when the fieldsOrder is filled the gridColumns should be sorted based on it
      this.gridColumns.sort((a, b) => {
        const indexA = fieldsOrder.indexOf(a.field);
        const indexB = fieldsOrder.indexOf(b.field);
        return indexA - indexB;
      });
    }

  }

  public loadRecords(event: TableLazyLoadEvent): void {
    // unsubscribe from previous request
    this._lastLoadRequest.unsubscribe();
    this._lastLoadEvent = event;
    this.loading = true;

    // stop if no data loader
    if (!this.dataLoader) {
      console.log('missing dataLoader');
      this.loading = false;
      return;
    }

    // create event data
    let eventData: LoadDataEvent = {
      includeCount: true,
      toSkip: event.first!,
      toTake: event.rows!,
      sortDirection: event.sortOrder ? event.sortOrder === 1 ? 'asc' : 'desc' : null,
      sortFieldName: Array.isArray(event.sortField) ? event.sortField[0] : event.sortField ?? null,
      filters: []
    };

    // set event filters
    if (event.filters) {
      for (let field in event.filters) {
        let filterMetadata = event.filters[field];

        if (Array.isArray(filterMetadata)) {
          filterMetadata.forEach(filter => {
            if (filter.value) {
              eventData.filters!.push({
                fieldName: field,
                filterValue: filter.value,
                filterOperator: filter.operator!
              });
            }
          });
        } else {
          if (filterMetadata!.value) {
            eventData.filters!.push({
              fieldName: field,
              filterValue: filterMetadata!.value,
              filterOperator: filterMetadata!.operator!
            });
          }
        }
      }
    }

    this._lastLoadRequest = this.dataLoader(eventData).subscribe(r => {
      this.loadedEntries = r.rows;
      this.initialLoadedEntries = JSON.parse(JSON.stringify(r.rows));
      if (eventData.includeCount) {
        this.totalRecords = r.totalCount!;
      }
      this.loading = false;
    });
  }

  public onSelectionChange(value = []): void {
    this.selectedEntries = value;
    this.scSelectionChanged.emit(this.selectedEntries);
  }

  public onSelectAllChange(event: { originalEvent: Event, checked: boolean }): void {
    this.scSelectionChanged.emit(this.selectedEntries);
  }

  public addNewRecord(newRecord: any): void {
    this.loadedEntries.unshift(newRecord);
  }

  public onEditComplete(event: any): void {
    let modifiedRecords = this.loadedEntries.filter((record, index) => {
      return JSON.stringify(record) !== JSON.stringify(this.initialLoadedEntries[index]);
    });

    if (modifiedRecords.length > 0) {
      modifiedRecords.forEach(r => {
        this.scOnDataChanged.emit([r]);
      });
    }
  }

  public editCell(rowIndex: number, fieldName: string): void {
    setTimeout(() => {
      let targetCell = this.editableColumns?.find(c => c.rowIndex == rowIndex && c.field === fieldName);
      if (!targetCell) {
        return;
      }
      targetCell.openCell();
    });
  }

  public closeOpenCell(): void {
    if (this.table.editingCell) {
      // find EditableColumn
      let editableColumn = this.editableColumns?.find(c => c.el.nativeElement === this.table.editingCell);
      editableColumn?.closeEditingCell(true, new Event('default'));
    }
  }

  public reload(): void {
    if (!this._lastLoadEvent) {
      return;
    }

    this.loadRecords(this._lastLoadEvent);
  }

  public onActionClick(column: ColumnSettings, record: any): void {
    column.actionSettings!.callback(record);
  }

  public removeRecord(record: any): void {
    // remove record from loaded entries
    let index = this.loadedEntries.indexOf(record);
    this.loadedEntries.splice(index, 1);
    this.totalRecords--;
  }
}
